import { useState, useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import DashboardWrapper from '../../Dashboard/components/DashboardWrapper'
import Toast from '../../../components/Toast/Toast'
import { TAGS } from '../../../services/Tags'
import {
  createRegisterFlag,
  getRegisterFlagsList,
  updateRegisterFlag,
  deleteRegisterFlag,
  getRegisteredTalentsByFlag,
} from '../../../services/RegisterFlag'

// ─── Constants ────────────────────────────────────────────────────────────────

const accent = '#01A76A'
const accentLight = 'rgba(1,167,106,0.08)'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SkeletonCard = () => (
  <div
    style={{
      borderRadius: 12,
      padding: '16px 18px',
      border: '1.5px solid #eef0f3',
      background: '#fafafa',
    }}
  >
    <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
    {[{ w: '35%', h: 14 }, { w: '95%', h: 11 }, { w: '60%', h: 11 }].map((s, i) => (
      <div
        key={i}
        style={{
          height: s.h,
          width: s.w,
          borderRadius: 6,
          background: 'linear-gradient(90deg,#e8ecf0 25%,#f5f6f8 50%,#e8ecf0 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.4s infinite',
          marginBottom: i < 2 ? 8 : 0,
        }}
      />
    ))}
  </div>
)

// ─── Form panel ───────────────────────────────────────────────────────────────

const FlagPanel = ({ editingFlag, onCancel, onCreate, onUpdate, isCreating, isUpdating, panelHeight }) => {
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm({
    defaultValues: { name: '', description: '', active: true },
  })

  const isEditing = Boolean(editingFlag)

  useEffect(() => {
    if (editingFlag) {
      setValue('name', editingFlag.name ?? '')
      setValue('description', editingFlag.description ?? '')
      setValue('active', editingFlag.active ?? true)
    } else {
      reset()
    }
  }, [editingFlag, setValue, reset])

  const handleCancel = () => { reset(); onCancel() }

  const onSubmit = (data) => {
    if (isEditing) {
      onUpdate({
        id: editingFlag.id,
        name: data.name,
        description: data.description,
        active: data.active,
      })
    } else {
      onCreate({ name: data.name, description: data.description })
      reset()
    }
  }

  const inputBase = {
    borderRadius: 8,
    fontSize: 13,
    border: '1.5px solid #e0e4ea',
    padding: '9px 13px',
    width: '100%',
    outline: 'none',
    background: '#fff',
    color: '#222',
    lineHeight: 1.5,
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  }

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 14,
        border: isEditing ? `2px solid ${accent}` : '1.5px solid #e8ecf0',
        boxShadow: isEditing ? `0 0 0 4px ${accentLight}` : '0 2px 10px rgba(0,0,0,0.05)',
        display: 'flex',
        flexDirection: 'column',
        height: panelHeight,
        transition: 'border-color 0.2s, box-shadow 0.2s',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '18px 22px 14px',
          borderBottom: '1px solid #f0f2f5',
          flexShrink: 0,
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 9,
              background: isEditing ? accentLight : '#f3f4f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
              flexShrink: 0,
            }}
          >
            {isEditing ? '✏️' : '➕'}
          </div>
          <div>
            <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: '#1a1a2e', lineHeight: 1.3 }}>
              {isEditing ? 'Editar flag de registro' : 'Nuevo flag de registro'}
            </h6>
            <span style={{ fontSize: 11, color: isEditing ? accent : '#aaa', fontWeight: 600 }}>
              {isEditing ? 'Modo edición activo' : 'Completa los campos y guarda'}
            </span>
          </div>
        </div>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', padding: '18px 22px' }}
      >
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 5 }}>
            Nombre <span style={{ color: '#e53935' }}>*</span>
          </label>
          <input
            style={inputBase}
            placeholder="Ej: Campaña LinkedIn Q3"
            {...register('name', { required: 'El nombre es obligatorio' })}
          />
          <ErrorMessage
            errors={errors}
            name="name"
            render={({ message }) => (
              <small style={{ color: '#e53935', fontSize: 11, marginTop: 4, display: 'block' }}>{message}</small>
            )}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 5 }}>
            Descripción
          </label>
          <textarea
            style={{ ...inputBase, flex: 1, resize: 'none', minHeight: 100 }}
            placeholder="Describe brevemente el propósito de este flag..."
            {...register('description')}
          />
        </div>

        {isEditing && (
          <div style={{ marginBottom: 16 }}>
            <label className="d-flex align-items-center gap-2" style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>
              <input type="checkbox" {...register('active')} />
              Activo
            </label>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
          <button
            type="submit"
            disabled={isCreating || isUpdating}
            style={{
              background: isCreating || isUpdating ? '#b2dfdb' : accent,
              color: '#fff',
              border: 'none',
              borderRadius: 9,
              padding: '10px 0',
              fontSize: 13,
              fontWeight: 700,
              cursor: isCreating || isUpdating ? 'not-allowed' : 'pointer',
              width: '100%',
              transition: 'background 0.2s',
              letterSpacing: '0.01em',
            }}
          >
            {isCreating || isUpdating ? 'Guardando...' : isEditing ? '✓ Actualizar flag' : '+ Agregar flag'}
          </button>
          {isEditing && (
            <button
              type="button"
              onClick={handleCancel}
              style={{
                background: '#f3f4f6',
                color: '#666',
                border: 'none',
                borderRadius: 9,
                padding: '10px 0',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                width: '100%',
              }}
            >
              Cancelar edición
            </button>
          )}
        </div>
      </form>
    </div>
  )
}

// ─── Flag card ────────────────────────────────────────────────────────────────

const FlagCard = ({ flag, isEditing, isSelected, onEdit, onDelete, onSelect, isDeleting }) => (
  <div
    style={{
      borderRadius: 12,
      padding: '14px 16px',
      border: isEditing || isSelected ? `1.5px solid ${accent}` : '1.5px solid #eaecf0',
      background: isEditing || isSelected ? accentLight : '#fff',
      transition: 'border-color 0.2s, background 0.2s, box-shadow 0.2s',
      boxShadow: isEditing || isSelected ? `0 0 0 3px ${accentLight}` : '0 1px 4px rgba(0,0,0,0.04)',
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      cursor: 'pointer',
    }}
    onClick={() => onSelect(flag)}
  >
    <div className="d-flex justify-content-between align-items-start gap-2">
      <div style={{ minWidth: 0 }}>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span style={{ fontWeight: 700, fontSize: 13, color: '#1a1a2e' }}>{flag.name}</span>
          {flag.active === false && (
            <span
              style={{
                background: 'rgba(0,0,0,0.04)',
                color: '#888',
                borderRadius: 20,
                padding: '2px 9px',
                fontSize: 10,
                fontWeight: 700,
              }}
            >
              Inactivo
            </span>
          )}
        </div>
        <span style={{ fontSize: 11, color: '#9aa' }}>id: {flag.id}</span>
      </div>
      <div className="d-flex gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={() => onEdit(flag)}
          style={{
            background: isEditing ? accent : '#f5f6f8',
            border: `1px solid ${isEditing ? accent : '#dde1e7'}`,
            borderRadius: 7,
            padding: '5px 13px',
            fontSize: 11,
            fontWeight: 700,
            cursor: 'pointer',
            color: isEditing ? '#fff' : '#444',
            whiteSpace: 'nowrap',
          }}
        >
          {isEditing ? '✓ Editando' : 'Editar'}
        </button>
        <button
          onClick={() => onDelete(flag.id)}
          disabled={isDeleting}
          style={{
            background: '#fff8f8',
            border: '1px solid #ffcdd2',
            borderRadius: 7,
            padding: '5px 13px',
            fontSize: 11,
            fontWeight: 700,
            cursor: isDeleting ? 'not-allowed' : 'pointer',
            color: '#e53935',
            whiteSpace: 'nowrap',
          }}
        >
          Eliminar
        </button>
      </div>
    </div>
    {flag.description && (
      <p style={{ fontSize: 12, color: '#556', margin: 0, lineHeight: 1.6 }}>{flag.description}</p>
    )}
  </div>
)

// ─── Registered talents panel ──────────────────────────────────────────────────

const RegisteredTalentsPanel = ({ flag, onClose }) => {
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(1)

  const { data, isLoading, isFetching } = useQuery(
    [TAGS.register_flag_talents, flag?.id, dateFrom, dateTo, page],
    () => getRegisteredTalentsByFlag({ flagId: flag.id, dateFrom, dateTo, page }),
    { enabled: Boolean(flag?.id) }
  )

  const talents = Array.isArray(data)
    ? data
    : data?.res?.list ?? data?.res ?? data?.list ?? []

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 14,
        border: '1.5px solid #e8ecf0',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        marginTop: 16,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '16px 22px',
          borderBottom: '1px solid #f0f2f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>
            Talentos registrados con: {flag?.name}
          </h6>
          <span style={{ fontSize: 11, color: '#9aa' }}>{talents.length} resultado(s)</span>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => { setPage(1); setDateFrom(e.target.value) }}
            style={{
              borderRadius: 8,
              border: '1.5px solid #e0e4ea',
              padding: '6px 10px',
              fontSize: 12,
              outline: 'none',
              color: '#333',
            }}
          />
          <span style={{ fontSize: 12, color: '#999' }}>a</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => { setPage(1); setDateTo(e.target.value) }}
            style={{
              borderRadius: 8,
              border: '1.5px solid #e0e4ea',
              padding: '6px 10px',
              fontSize: 12,
              outline: 'none',
              color: '#333',
            }}
          />
          <button
            onClick={onClose}
            style={{
              background: '#f3f4f6',
              color: '#666',
              border: 'none',
              borderRadius: 8,
              padding: '7px 14px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Cerrar
          </button>
        </div>
      </div>

      <div style={{ padding: '14px 22px', maxHeight: 340, overflowY: 'auto' }}>
        {isLoading || isFetching ? (
          <div style={{ display: 'grid', gap: 10 }}>
            {[1, 2, 3].map((k) => <SkeletonCard key={k} />)}
          </div>
        ) : talents.length === 0 ? (
          <p style={{ color: '#aaa', fontSize: 13, textAlign: 'center', padding: '20px 0', margin: 0 }}>
            No hay talentos registrados con este flag en el rango seleccionado.
          </p>
        ) : (
          <table className="w-100" style={{ borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#888' }}>
                <th style={{ padding: '6px 8px', fontWeight: 700 }}>Nombre</th>
                <th style={{ padding: '6px 8px', fontWeight: 700 }}>Email</th>
                <th style={{ padding: '6px 8px', fontWeight: 700 }}>Fecha de registro</th>
              </tr>
            </thead>
            <tbody>
              {talents.map((talent) => (
                <tr key={talent.id} style={{ borderTop: '1px solid #f0f2f5' }}>
                  <td style={{ padding: '8px', fontWeight: 600, color: '#1a1a2e' }}>
                    {talent.name ?? talent.fullName ?? '—'}
                  </td>
                  <td style={{ padding: '8px', color: '#556' }}>{talent.email ?? '—'}</td>
                  <td style={{ padding: '8px', color: '#556' }}>
                    {talent.createdAt ?? talent.registeredAt ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="d-flex justify-content-end align-items-center gap-2" style={{ padding: '10px 22px', borderTop: '1px solid #f0f2f5' }}>
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1}
          style={{
            background: '#f5f6f8',
            border: '1px solid #dde1e7',
            borderRadius: 7,
            padding: '5px 13px',
            fontSize: 11,
            fontWeight: 700,
            cursor: page <= 1 ? 'not-allowed' : 'pointer',
            color: '#444',
          }}
        >
          Anterior
        </button>
        <span style={{ fontSize: 12, color: '#666', fontWeight: 600 }}>Página {page}</span>
        <button
          onClick={() => setPage((p) => p + 1)}
          disabled={talents.length === 0}
          style={{
            background: '#f5f6f8',
            border: '1px solid #dde1e7',
            borderRadius: 7,
            padding: '5px 13px',
            fontSize: 11,
            fontWeight: 700,
            cursor: talents.length === 0 ? 'not-allowed' : 'pointer',
            color: '#444',
          }}
        >
          Siguiente
        </button>
      </div>
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

const PANEL_HEIGHT = 'calc(100vh - 220px)'

const pageStyles = `
  .register-flags-wrapper.dashboard-wrapper {
    margin-left: 24px !important;
    margin-right: 24px !important;
    min-width: 0;
  }
`

const RegisterFlagsPage = () => {
  const queryClient = useQueryClient()
  const [editingFlag, setEditingFlag] = useState(null)
  const [selectedFlag, setSelectedFlag] = useState(null)
  const [search, setSearch] = useState('')

  const { data: flagsData, isLoading } = useQuery(
    [TAGS.register_flags],
    getRegisterFlagsList
  )

  const rawFlags = Array.isArray(flagsData)
    ? flagsData
    : flagsData?.flags ?? flagsData?.res?.list ?? flagsData?.res ?? flagsData?.list ?? []

  const flags = search.trim()
    ? rawFlags.filter(
        (item) =>
          item.name?.toLowerCase().includes(search.toLowerCase()) ||
          item.description?.toLowerCase().includes(search.toLowerCase())
      )
    : rawFlags

  const invalidate = () => queryClient.invalidateQueries([TAGS.register_flags])

  const { mutate: createFlag, isLoading: isCreating } = useMutation(createRegisterFlag, {
    onSuccess: () => { invalidate(); Toast({ title: 'Flag de registro creado correctamente' }) },
  })

  const { mutate: updateFlag, isLoading: isUpdating } = useMutation(updateRegisterFlag, {
    onSuccess: () => {
      invalidate()
      setEditingFlag(null)
      Toast({ title: 'Flag de registro actualizado correctamente' })
    },
  })

  const { mutate: deleteFlag, isLoading: isDeleting } = useMutation(deleteRegisterFlag, {
    onSuccess: () => {
      invalidate()
      setSelectedFlag(null)
      Toast({ title: 'Flag de registro eliminado', icon: 'info' })
    },
  })

  const startEdit = useCallback((flag) => setEditingFlag(flag), [])
  const cancelEdit = useCallback(() => setEditingFlag(null), [])
  const selectFlag = useCallback(
    (flag) => setSelectedFlag((prev) => (prev?.id === flag.id ? null : flag)),
    []
  )

  return (
    <>
      <style>{pageStyles}</style>
      <DashboardWrapper dashboardWrapperClassName="flex-grow-1 register-flags-wrapper">
        <div
          className="pt-5 mt-5"
          style={{
            background: 'linear-gradient(135deg, #01A76A 0%, #019960 100%)',
            borderRadius: 14,
            padding: '24px 32px',
            marginBottom: 20,
            color: '#fff',
          }}
        >
          <h4 style={{ fontWeight: 700, margin: 0, fontSize: 20 }}>Flags de registro</h4>
          <p style={{ margin: '6px 0 0', opacity: 0.85, fontSize: 14 }}>
            Gestiona los flags usados para etiquetar el origen de registro de los talentos y consulta quién se registró con cada uno.
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 2fr',
            gap: 20,
            alignItems: 'start',
            width: '100%',
          }}
        >
          <FlagPanel
            editingFlag={editingFlag}
            onCancel={cancelEdit}
            onCreate={createFlag}
            onUpdate={updateFlag}
            isCreating={isCreating}
            isUpdating={isUpdating}
            panelHeight={PANEL_HEIGHT}
          />

          <div
            style={{
              background: '#fff',
              borderRadius: 14,
              border: '1.5px solid #e8ecf0',
              boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
              display: 'flex',
              flexDirection: 'column',
              height: PANEL_HEIGHT,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '18px 22px 14px',
                borderBottom: '1px solid #f0f2f5',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div className="d-flex align-items-center gap-2">
                <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>
                  Flags
                </h6>
                {!isLoading && (
                  <span
                    style={{
                      background: accentLight,
                      color: accent,
                      borderRadius: 20,
                      padding: '2px 10px',
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {flags.length}
                  </span>
                )}
              </div>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar flag..."
                style={{
                  borderRadius: 8,
                  border: '1.5px solid #e0e4ea',
                  padding: '7px 13px',
                  fontSize: 12,
                  outline: 'none',
                  width: 220,
                  color: '#333',
                  background: '#fafbfc',
                }}
              />
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px' }}>
              {isLoading ? (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: 14,
                  }}
                >
                  {[1, 2, 3, 4].map((k) => <SkeletonCard key={k} />)}
                </div>
              ) : flags.length === 0 ? (
                <div
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                    color: '#aaa',
                    paddingTop: 40,
                  }}
                >
                  <span style={{ fontSize: 44 }}>🚩</span>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 500 }}>
                    {search ? 'Sin resultados para esa búsqueda.' : 'No hay flags de registro creados aún.'}
                  </p>
                  {search && (
                    <button
                      onClick={() => setSearch('')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: accent,
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    >
                      Limpiar búsqueda
                    </button>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: 14,
                    alignContent: 'start',
                  }}
                >
                  {flags.map((flag) => (
                    <FlagCard
                      key={flag.id}
                      flag={flag}
                      isEditing={editingFlag?.id === flag.id}
                      isSelected={selectedFlag?.id === flag.id}
                      onEdit={startEdit}
                      onDelete={deleteFlag}
                      onSelect={selectFlag}
                      isDeleting={isDeleting}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {selectedFlag && (
          <RegisteredTalentsPanel flag={selectedFlag} onClose={() => setSelectedFlag(null)} />
        )}
      </DashboardWrapper>
    </>
  )
}

export default RegisterFlagsPage
